import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Check, X, Pause, ClipboardCheck, FileText, ExternalLink, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { getApprovalQueue, approveOrderItems, getFirms, revertItemDecision } from '../../services/taskApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FlowApprovals = () => {
  const { isDark } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [qtyEdits, setQtyEdits] = useState({}); // item_id -> qty (for partial approve)
  const [remarkEdits, setRemarkEdits] = useState({}); // item_id -> admin_remarks
  const [processing, setProcessing] = useState(null); // item_id being processed

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, fRes] = await Promise.all([getApprovalQueue(filterCompany || undefined, includeResolved), getFirms()]);
      setOrders(oRes.data || []);
      setFirms(fRes.data || []);
    } catch (err) { console.error('FlowApprovals: fetch failed', err); }
    finally { setLoading(false); }
  }, [filterCompany, includeResolved]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (orderId, item, action) => {
    const approvedQty = action === 'Approved'
      ? (parseInt(qtyEdits[item.id]) || item.quantity_ordered)
      : 0;
    if (action === 'Approved' && (approvedQty <= 0 || approvedQty > item.quantity_ordered)) {
      toast.error(`Approved qty must be 1 to ${item.quantity_ordered}`); return;
    }
    // Confirm dialog for destructive Reject / Hold actions
    const adminRemarks = (remarkEdits[item.id] || '').trim();
    if (action === 'Rejected') {
      const ok = window.confirm(
        `REJECT "${item.item_name}" (${item.quantity_ordered} units)?\n\n` +
        `This stops the item from going to Store/Purchase. The sales agent will need to edit and resubmit.\n\n` +
        `Click OK to reject${adminRemarks ? ' with the remark you typed' : ' (no remarks)'}.`
      );
      if (!ok) return;
    } else if (action === 'Hold' && !adminRemarks) {
      // Hold without remarks is reversible but suspicious — gently nudge
      if (!window.confirm(`Mark "${item.item_name}" as Hold without any remarks?\n\nClick OK to proceed, Cancel to add remarks first.`)) return;
    }
    setProcessing(item.id);
    try {
      const res = await approveOrderItems(orderId, [{
        item_id: item.id,
        action,
        approved_qty: approvedQty,
        admin_remarks: adminRemarks,
      }]);
      toast.success(`${item.item_name} → ${action}${action === 'Approved' ? ` (${approvedQty})` : ''}`);
      if (res.data?.new_status === 'Fully Approved' || res.data?.new_status === 'SO Rejected') {
        toast.message(`SO ${res.data.new_status}`);
      }
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const handleBulkApprove = async (order) => {
    const decisions = order.items
      .filter(it => it.approval_status === 'Pending')
      .map(it => ({
        item_id: it.id,
        action: 'Approved',
        approved_qty: parseInt(qtyEdits[it.id]) || it.quantity_ordered,
        admin_remarks: (remarkEdits[it.id] || '').trim(),
      }));
    if (!decisions.length) { toast.error('No pending items'); return; }
    setProcessing(order.id);
    try {
      const res = await approveOrderItems(order.id, decisions);
      toast.success(`Approved ${decisions.length} items — ${res.data.new_status}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const handleRevert = async (item) => {
    if (!window.confirm(`Revert "${item.item_name}" from ${item.approval_status} back to Pending?`)) return;
    setProcessing(item.id);
    try {
      await revertItemDecision(item.id);
      toast.success(`${item.item_name} reverted to Pending`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const getFirmName = (id) => firms.find(f => f.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="flow-approvals">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Approval Queue</h1>
          <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Click ✓/✗/⏸ per line — action is immediate</p>
        </div>
        <div className="flex items-center gap-3">
          <label className={`flex items-center gap-2 text-xs cursor-pointer ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            <input type="checkbox" checked={includeResolved} onChange={e => setIncludeResolved(e.target.checked)} data-testid="show-resolved-toggle" />
            Show resolved (to revert)
          </label>
          <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className={`h-40 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : orders.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <ClipboardCheck size={40} className={`mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>No orders pending approval</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {orders.map(o => {
            const pendingItems = (o.items || []).filter(it => it.approval_status === 'Pending');
            return (
            <Card key={o.id} className={cardCls}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {o.so_number} — {o.customer_name}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                      {getFirmName(o.company_id)} | Agent: {o.agent_name} | {o.created_at?.split('T')[0]} | ₹{(o.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {o.po_proof_url && (
                      <a href={o.po_proof_url.startsWith("http") ? o.po_proof_url : `${API_URL}${o.po_proof_url.startsWith("/api/") ? "" : "/api/files/"}${o.po_proof_url}`} target="_blank" rel="noreferrer" className="text-blue-400 text-xs flex items-center gap-1 hover:underline">
                        <FileText size={14} /> PO Proof <ExternalLink size={12} />
                      </a>
                    )}
                    {pendingItems.length > 1 && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" disabled={processing === o.id} onClick={() => handleBulkApprove(o)} data-testid={`bulk-approve-${o.id}`}>
                        {processing === o.id ? '...' : `Approve All (${pendingItems.length})`}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[840px]">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>#</th>
                        <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item / Description</th>
                        <th className={`text-center py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Qty</th>
                        <th className={`text-right py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>List / Disc</th>
                        <th className={`text-right py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Net Price</th>
                        <th className={`text-center py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Approve Qty</th>
                        <th className={`text-center py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(o.items || []).map((item, i) => {
                        const isPending = item.approval_status === 'Pending';
                        const isProc = processing === item.id;
                        return (
                          <tr key={item.id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                            <td className={`py-2.5 px-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{i + 1}</td>
                            <td className={`py-2.5 px-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <div className="font-medium">{item.item_name}</div>
                              {item.pending_elsewhere && (
                                <div className="mt-0.5 text-[10px] px-2 py-1 rounded border-l-2 border-red-500 bg-red-500/10 text-red-400 font-medium" data-testid={`pending-elsewhere-${item.id}`}>
                                  ⚠ {item.pending_elsewhere.pending_qty} qty of this item still PENDING on {item.pending_elsewhere.so_number} ({item.pending_elsewhere.status}) — verify genuineness to avoid over-stocking
                                </div>
                              )}
                              {item.description && <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{item.description}</div>}
                              {item.drawing_url && (
                                <a href={item.drawing_url.startsWith("http") ? item.drawing_url : `${API_URL}${item.drawing_url.startsWith("/api/") ? "" : "/api/files/"}${item.drawing_url}`} target="_blank" rel="noreferrer" className="text-[10px] text-blue-400 hover:underline">View Drawing</a>
                              )}
                              {item.admin_remarks && (
                                <div className={`mt-1 text-[10px] px-2 py-1 rounded border-l-2 border-amber-500 ${isDark ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-800'}`} data-testid={`remarks-display-${item.id}`} title={`By ${item.admin_remarks_by || '-'}${item.admin_remarks_at ? ' on ' + item.admin_remarks_at.split('T')[0] : ''}`}>
                                  💬 <span className="font-medium">Admin:</span> {item.admin_remarks}
                                </div>
                              )}
                              {isPending && (
                                <Input
                                  type="text" maxLength={500}
                                  placeholder="Admin remarks (visible in Store & Purchase)"
                                  className={`mt-1.5 h-7 text-[11px] ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-200'}`}
                                  value={remarkEdits[item.id] || ''}
                                  onChange={e => setRemarkEdits(r => ({ ...r, [item.id]: e.target.value }))}
                                  data-testid={`remarks-input-${item.id}`}
                                />
                              )}
                            </td>
                            <td className={`py-2.5 px-2 text-center font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.quantity_ordered}</td>
                            <td className={`py-2.5 px-2 text-right ${isDark ? 'text-white/60' : 'text-gray-600'}`} data-testid={`list-price-${item.id}`}>
                              {item.list_price ? (
                                <>
                                  <div>₹{(item.list_price || 0).toLocaleString('en-IN')}</div>
                                  {(item.discount_percent || 0) > 0 && (
                                    <div className="text-[10px] font-semibold text-amber-500">-{item.discount_percent}%</div>
                                  )}
                                </>
                              ) : <span className={isDark ? 'text-white/20' : 'text-gray-300'}>—</span>}
                            </td>
                            <td className={`py-2.5 px-2 text-right ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                              ₹{(item.net_price || item.unit_price || 0).toLocaleString('en-IN')}
                              {item.last_customer_price && (
                                <div
                                  className={`text-[10px] mt-0.5 font-medium ${(item.last_customer_price.net_price || 0) < (item.net_price || 0) ? 'text-red-400' : 'text-green-500'}`}
                                  title={`Last sold to this customer on ${item.last_customer_price.at}${item.last_customer_price.qty ? ` (qty ${item.last_customer_price.qty})` : ''}`}
                                  data-testid={`last-price-${item.id}`}
                                >
                                  Last: ₹{(item.last_customer_price.net_price || 0).toLocaleString('en-IN')}
                                  {item.last_customer_price.so_number && <span className="opacity-60"> · {item.last_customer_price.so_number}</span>}
                                </div>
                              )}
                            </td>
                            <td className="py-2.5 px-2 text-center">
                              {isPending ? (
                                <Input
                                  type="number" min="1" max={item.quantity_ordered}
                                  className={`w-20 h-7 text-xs text-center mx-auto ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
                                  defaultValue={item.quantity_ordered}
                                  onChange={e => setQtyEdits(q => ({ ...q, [item.id]: e.target.value }))}
                                />
                              ) : <span className={isDark ? 'text-white/50' : 'text-gray-500'}>{item.quantity_approved || '-'}</span>}
                            </td>
                            <td className="py-2.5 px-2">
                              {isPending ? (
                                <div className="flex items-center justify-center gap-1.5">
                                  <Button size="sm" disabled={isProc}
                                    className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => handleAction(o.id, item, 'Approved')} data-testid={`approve-${item.id}`}>
                                    {isProc ? '...' : <><Check size={14} /></>}
                                  </Button>
                                  <Button size="sm" disabled={isProc}
                                    className="h-7 px-2 bg-red-600 hover:bg-red-700 text-white"
                                    onClick={() => handleAction(o.id, item, 'Rejected')} data-testid={`reject-${item.id}`}>
                                    <X size={14} />
                                  </Button>
                                  <Button size="sm" disabled={isProc}
                                    className="h-7 px-2 bg-gray-500 hover:bg-gray-600 text-white"
                                    onClick={() => handleAction(o.id, item, 'Hold')} data-testid={`hold-${item.id}`}>
                                    <Pause size={14} />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-2">
                                  <span className={`text-xs font-medium ${item.approval_status === 'Approved' ? 'text-green-400' : item.approval_status === 'Rejected' ? 'text-red-400' : 'text-gray-400'}`}>
                                    {item.approval_status}
                                  </span>
                                  {(item.approval_status === 'Rejected' || item.approval_status === 'Hold') && (
                                    <Button size="sm" variant="ghost" disabled={processing === item.id}
                                      className={`h-6 px-2 text-[10px] ${isDark ? 'text-blue-400 hover:bg-blue-500/10' : 'text-blue-600 hover:bg-blue-50'}`}
                                      onClick={() => handleRevert(item)} data-testid={`revert-${item.id}`}>
                                      <RotateCcw size={11} className="mr-1" /> Revert
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FlowApprovals;
